class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
      '#SortBy-mobile-outside',
      '[data-lt-u2-apply]',
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
      if (selector === '[data-lt-u2-apply]') {
        targetElement.dataset.resultCount = sourceElement.dataset.resultCount || '0';
      }
    });

    const mobileDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (mobileDrawer && !mobileDrawer.closest('lt-mobile-facets')) mobileDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest('form');

    if (sourceForm?.id === 'FacetSortMobileForm') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', event.target.value);
      searchParams.delete('page');
      this.onSubmitForm(searchParams.toString(), event);
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class LtMobileFacets extends HTMLElement {
  constructor() {
    super();
    this.rootDetails = this.querySelector('[data-lt-u2-root-details]');
    this.trigger = this.rootDetails?.querySelector(':scope > summary');
    this.form = this.querySelector('#FacetFiltersFormMobile');
    this.snapshot = [];
    this.snapshotApplyLabel = '';
    this.snapshotApplyCount = '';
    this.countRequest = null;
    this.countTimer = null;
    this.historyMarkerActive = false;
    this.closingFromPopstate = false;
    this.applying = false;

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onPopstate = this.onPopstate.bind(this);
  }

  connectedCallback() {
    if (!this.rootDetails || !this.trigger || !this.form || this.dataset.ltU2Bound === 'true') return;
    this.dataset.ltU2Bound = 'true';
    this.addEventListener('click', this.onClick, true);
    this.addEventListener('input', this.onInput, true);
    this.addEventListener('keyup', this.onKeyUp, true);
    this.addEventListener('submit', this.onSubmit, true);
    this.rootDetails.addEventListener('toggle', this.onToggle);
    window.addEventListener('popstate', this.onPopstate);
    this.syncStagedVisuals();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick, true);
    this.removeEventListener('input', this.onInput, true);
    this.removeEventListener('keyup', this.onKeyUp, true);
    this.removeEventListener('submit', this.onSubmit, true);
    this.rootDetails?.removeEventListener('toggle', this.onToggle);
    window.removeEventListener('popstate', this.onPopstate);
    this.countRequest?.abort();
    clearTimeout(this.countTimer);
  }

  onClick(event) {
    const closeButton = event.target.closest('[data-lt-u2-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer();
      return;
    }

    const clearButton = event.target.closest('[data-lt-u2-clear]');
    if (clearButton && this.contains(clearButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.clearStagedFilters();
      return;
    }

    const applyButton = event.target.closest('[data-lt-u2-apply]');
    if (applyButton && this.contains(applyButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.applyStagedFilters(event);
      return;
    }

    const groupSummary = event.target.closest('.lt-u2-mobile-facets__details > summary');
    const groupDetails = groupSummary?.parentElement;
    if (groupSummary && groupDetails && this.contains(groupDetails)) {
      event.preventDefault();
      event.stopPropagation();
      groupDetails.open = !groupDetails.open;
      groupSummary.setAttribute('aria-expanded', groupDetails.open ? 'true' : 'false');
    }
  }

  onInput(event) {
    if (!event.target.closest('#FacetFiltersFormMobile')) return;
    event.stopPropagation();
    this.syncStagedVisuals();
    this.scheduleCountPreview();
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.rootDetails.open) return;
    event.preventDefault();
    event.stopPropagation();
    this.closeDrawer();
  }

  onSubmit(event) {
    if (event.target !== this.form) return;
    event.preventDefault();
    event.stopPropagation();
    this.applyStagedFilters(event);
  }

  onToggle() {
    if (this.rootDetails.open) {
      this.snapshotFormState();
      this.openHistoryMarker();
      document.body.classList.add('lt-u2-mobile-facets-open');
      requestAnimationFrame(() => this.querySelector('[data-lt-u2-close]')?.focus());
      return;
    }

    this.countRequest?.abort();
    clearTimeout(this.countTimer);
    document.body.classList.remove('lt-u2-mobile-facets-open');

    if (!this.applying) this.restoreFormState();

    if (this.historyMarkerActive && !this.closingFromPopstate) {
      this.historyMarkerActive = false;
      history.back();
    } else {
      this.historyMarkerActive = false;
    }

    this.closingFromPopstate = false;
    this.applying = false;
    this.trigger.focus();
  }

  onPopstate() {
    if (!this.rootDetails.open || !this.historyMarkerActive) return;
    this.closingFromPopstate = true;
    this.historyMarkerActive = false;
    this.closeDrawer();
  }

  openHistoryMarker() {
    if (this.historyMarkerActive) return;
    const searchParams = window.location.search.slice(1);
    history.pushState(
      { ...(history.state || {}), searchParams, ltU2Drawer: true },
      '',
      `${window.location.pathname}${window.location.search}`,
    );
    this.historyMarkerActive = true;
  }

  closeDrawer() {
    if (!this.rootDetails.open) return;
    this.trigger.click();
  }

  snapshotFormState() {
    this.snapshot = Array.from(this.form.elements).map((element) => ({
      element,
      checked: 'checked' in element ? element.checked : undefined,
      value: element.value,
    }));
    const applyButton = this.querySelector('[data-lt-u2-apply]');
    this.snapshotApplyLabel = applyButton?.textContent || '';
    this.snapshotApplyCount = applyButton?.dataset.resultCount || '';
  }

  restoreFormState() {
    this.snapshot.forEach(({ element, checked, value }) => {
      if (!element.isConnected) return;
      if (checked !== undefined) element.checked = checked;
      else element.value = value;
    });
    const applyButton = this.querySelector('[data-lt-u2-apply]');
    if (applyButton) {
      applyButton.textContent = this.snapshotApplyLabel;
      applyButton.dataset.resultCount = this.snapshotApplyCount;
    }
    this.syncStagedVisuals();
  }

  clearStagedFilters() {
    this.form.querySelectorAll('[name^="filter."]').forEach((element) => {
      if (element.type === 'checkbox' || element.type === 'radio') element.checked = false;
      else element.value = '';
    });
    this.syncStagedVisuals();
    this.scheduleCountPreview(true);
  }

  syncStagedVisuals() {
    this.form.querySelectorAll('.mobile-facets__label').forEach((label) => {
      const input = label.querySelector('input[type="checkbox"]');
      if (input) label.classList.toggle('active', input.checked);
    });

    this.form.querySelectorAll('[data-lt-u2-allergy-help]').forEach((help) => {
      const input = document.getElementById(help.dataset.ltU2ForInput);
      help.hidden = !input?.checked;
    });

    const hasStagedFilters = Array.from(this.form.querySelectorAll('[name^="filter."]')).some((element) =>
      element.type === 'checkbox' || element.type === 'radio' ? element.checked : Boolean(element.value),
    );
    const clearButton = this.querySelector('[data-lt-u2-clear]');
    clearButton?.classList.toggle('disabled', !hasStagedFilters);
    clearButton?.setAttribute('aria-disabled', hasStagedFilters ? 'false' : 'true');
  }

  createSearchParams() {
    return new URLSearchParams(new FormData(this.form)).toString();
  }

  scheduleCountPreview(immediate = false) {
    clearTimeout(this.countTimer);
    this.countTimer = setTimeout(() => this.previewResultCount(), immediate ? 0 : 250);
  }

  async previewResultCount() {
    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) return;

    this.countRequest?.abort();
    this.countRequest = new AbortController();
    const applyButton = this.querySelector('[data-lt-u2-apply]');
    applyButton?.setAttribute('aria-busy', 'true');

    try {
      const searchParams = this.createSearchParams();
      const response = await fetch(
        `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&${searchParams}`,
        { signal: this.countRequest.signal },
      );
      if (!response.ok) throw new Error(`Filter count request failed: ${response.status}`);
      const html = await response.text();
      const countElement = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
      if (!countElement) throw new Error('Filter count response is missing ProductCount');
      this.setApplyCount(parseInt(countElement.dataset.productCount, 10) || 0);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      applyButton?.removeAttribute('aria-busy');
    }
  }

  setApplyCount(count) {
    const applyButton = this.querySelector('[data-lt-u2-apply]');
    if (!applyButton) return;
    applyButton.dataset.resultCount = String(count);
    applyButton.textContent = LtMobileFacets.formatApplyLabel(count);
  }

  applyStagedFilters(event) {
    const searchParams = this.createSearchParams();
    const nextState = { ...(history.state || {}), searchParams };
    delete nextState.ltU2Drawer;

    this.applying = true;
    this.historyMarkerActive = false;
    history.replaceState(
      nextState,
      '',
      `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`,
    );
    FacetFiltersForm.renderPage(searchParams, event, false);
    this.closeDrawer();
  }

  static formatApplyLabel(count) {
    if (count === 1) return 'Prikaži 1 izdelek';
    if (count === 2) return 'Prikaži 2 izdelka';
    if (count === 3 || count === 4) return `Prikaži ${count} izdelke`;
    return `Prikaži ${count} izdelkov`;
  }
}

customElements.define('lt-mobile-facets', LtMobileFacets);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
